/**
 * Google Analytics 4 Service
 * Centralized tracking for ClothedEye application
 */

import logger from '../utils/logger.js';

// Check if gtag is available (Google Analytics loaded)
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && window.gtag;
};

/**
 * Track page views
 * @param {string} pageTitle - Page title
 * @param {string} pagePath - Page path
 */
export const trackPageView = (pageTitle, pagePath) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('config', 'G-QCTWCYPLDS', {
      page_title: pageTitle,
      page_location: window.location.origin + pagePath,
      page_path: pagePath,
    });
    // logger.log('📊 Analytics: Page view tracked', { pageTitle, pagePath });
  } catch (error) {
    console.error('❌ Analytics: Failed to track page view', error);
  }
};

/**
 * Track custom events
 * @param {string} eventName - Event name
 * @param {Object} parameters - Event parameters
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('event', eventName, parameters);
    // logger.log('📊 Analytics: Event tracked', { eventName, parameters });
  } catch (error) {
    console.error('❌ Analytics: Failed to track event', error);
  }
};

/**
 * Track Etsy listing clicks (conversions)
 * @param {string} listingId - Etsy listing ID
 * @param {string} listingTitle - Listing title
 * @param {string} price - Listing price
 * @param {string} size - Sticker size
 */
export const trackEtsyClick = (listingId, listingTitle, price, size) => {
  // Only include parameters that have meaningful values
  const parameters = {
    listing_id: listingId,
    currency: 'USD',
    content_type: 'sticker',
    timestamp: new Date().toISOString(),
  };

  // Only add title if it exists and isn't empty (truncate long titles)
  if (listingTitle && listingTitle.trim() !== '') {
    parameters.listing_title =
      listingTitle.length > 20 ? listingTitle.substring(0, 20) : listingTitle;
  }

  // Only add price if it exists and isn't empty
  if (price && price.trim() !== '' && price !== 'Unknown') {
    parameters.price = price;
  }

  // Only add size if it exists and isn't empty
  if (size && size.trim() !== '' && size !== 'Unknown') {
    parameters.size = size;
  }

  trackEvent('etsy_click', parameters);
};

/**
 * Track Scene Builder interactions
 * @param {string} action - Action performed (add_sticker, remove_sticker, etc.)
 * @param {string} stickerId - Sticker identifier
 * @param {string} category - Sticker category
 */
export const trackSceneBuilderAction = (action, stickerId, category, listingTitle = null) => {
  // Only include parameters that have meaningful values
  const parameters = {
    action: action,
    timestamp: new Date().toISOString(),
  };

  // Only add sticker_id if it exists and isn't empty
  if (stickerId && stickerId.trim() !== '') {
    parameters.sticker_id = stickerId;
  }

  // Only add category if it exists and isn't empty
  if (category && category.trim() !== '') {
    parameters.category = category;
  }

  // Add listing title if provided (truncate long titles)
  if (listingTitle && listingTitle.trim() !== '') {
    parameters.listing_title =
      listingTitle.length > 20 ? listingTitle.substring(0, 20) : listingTitle;
  }

  trackEvent('scene_builder_action', parameters);
};

/**
 * Track shopping list interactions
 * @param {string} action - Action performed (view_list, export_list, etc.)
 * @param {number} itemCount - Number of items in list
 * @param {number} totalPrice - Total price (if available)
 */
export const trackShoppingListAction = (action, itemCount, totalPrice) => {
  trackEvent('shopping_list_action', {
    action: action,
    item_count: itemCount,
    total_price: totalPrice || 0,
    currency: 'USD',
  });
};

/**
 * Track search and filter usage
 * @param {string} searchType - Type of search (text, category, sort)
 * @param {string} value - Search/filter value
 * @param {string} resultCount - Number of results
 */
export const trackSearchFilter = (searchType, value, resultCount) => {
  trackEvent('search_filter', {
    search_type: searchType,
    value: value,
    result_count: resultCount,
  });
};

/**
 * Track user engagement with stickers
 * @param {string} stickerId - Sticker identifier
 * @param {string} action - Action performed (view, click, add_to_scene)
 * @param {string} category - Sticker category
 * @param {string} size - Sticker size
 */
export const trackStickerEngagement = (stickerId, action, category, size, listingTitle = null) => {
  // Only include parameters that have meaningful values
  const parameters = {
    sticker_id: stickerId,
    action: action,
    timestamp: new Date().toISOString(),
  };

  // Only add category if it exists and isn't empty
  if (category && category.trim() !== '') {
    parameters.category = category;
  }

  // Only add size if it exists and isn't empty
  if (size && size.trim() !== '') {
    parameters.size = size;
  }

  // Add listing title if provided (truncate long titles)
  if (listingTitle && listingTitle.trim() !== '') {
    parameters.listing_title =
      listingTitle.length > 20 ? listingTitle.substring(0, 20) : listingTitle;
  }

  trackEvent('sticker_engagement', parameters);
};

/**
 * Track error occurrences
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} component - Component where error occurred
 */
export const trackError = (errorType, errorMessage, component) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    component: component,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track performance metrics
 * @param {string} metricName - Performance metric name
 * @param {number} value - Metric value
 * @param {string} unit - Unit of measurement
 */
export const trackPerformance = (metricName, value, unit) => {
  trackEvent('performance', {
    metric_name: metricName,
    value: value,
    unit: unit,
    timestamp: new Date().toISOString(),
  });
};

export default {
  trackPageView,
  trackEvent,
  trackEtsyClick,
  trackSceneBuilderAction,
  trackShoppingListAction,
  trackSearchFilter,
  trackStickerEngagement,
  trackError,
  trackPerformance,
};
